import { InlineNotificationProps } from '@moneyforward/ap-frontend-components';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { PROGRESS_STEP } from '../constants';

type SelectedFile = {
  invoiceId: string;
  fileUrl: string;
};

/**
 * Context type for shared data in UtilizePastDetails.
 */
export type DataContextType = {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  notificationDrawer: InlineNotificationProps | null;
  setNotificationDrawer: (notification: InlineNotificationProps | null) => void;
  currentStep: (typeof PROGRESS_STEP)[keyof typeof PROGRESS_STEP] | null;
  onSetCurrentStep: (
    step: (typeof PROGRESS_STEP)[keyof typeof PROGRESS_STEP] | null
  ) => void;
  onSetSelectedFile: (selectedFile: SelectedFile | null) => void;
  onCloseDialog: () => void;
  onCancel: () => void;
  selectedFile: SelectedFile | null;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

/**
 * Hook to access the DataContext. Must be used within a DataProvider.
 */
export const useDataContext = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useDataContext must be used within DataProvider');
  return ctx;
};

/**
 * Provider for DataContext. Wrap your subtree with this to provide shared state.
 */
export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [notificationDrawer, setNotificationDrawer] =
    useState<InlineNotificationProps | null>(null);
  const [currentStep, setCurrentStep] = useState<
    (typeof PROGRESS_STEP)[keyof typeof PROGRESS_STEP] | null
  >(null);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);

  const onSetCurrentStep = useCallback(
    (step: (typeof PROGRESS_STEP)[keyof typeof PROGRESS_STEP] | null) => {
      setCurrentStep(step);
    },
    []
  );

  const onCloseDialog = useCallback(() => {
    if (currentStep === PROGRESS_STEP.ON_OPEN_DIALOG) {
      setCurrentStep(null);
      setNotificationDrawer(null);
    } else {
      setCurrentStep(currentStep);
    }
  }, [currentStep]);

  const onSetSelectedFile = useCallback((selectedFile: SelectedFile | null) => {
    setSelectedFile(selectedFile);
  }, []);

  const onCancel = useCallback(() => {
    setCurrentStep(null);
    setSelectedFile(null);
    setNotificationDrawer(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      notificationDrawer,
      setNotificationDrawer,
      currentStep,
      onSetCurrentStep,
      onSetSelectedFile,
      selectedFile,
      onCloseDialog,
      onCancel,
    }),
    [
      isLoading,
      notificationDrawer,
      currentStep,
      onSetSelectedFile,
      selectedFile,
      setIsLoading,
      setNotificationDrawer,
      onSetCurrentStep,
      onCloseDialog,
      onCancel,
    ]
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};
